import { useEffect, useRef } from "react";

export type CollageImage = {
  src: string;
  alt: string;
  /** Relative width of the tile within the row. Defaults to 1 (landscape). */
  span?: number;
  /** Render as a muted looping video instead of an image. */
  video?: boolean;
};

type Props = {
  rowOne: CollageImage[];
  rowTwo: CollageImage[];
  /** px of horizontal drift per px of scroll. Row one moves left, row two right. */
  speedOne?: number;
  speedTwo?: number;
  className?: string;
};

const BASE_TILE = 520; // px width for a span-1 tile at md+

export default function ParallaxCollage({
  rowOne,
  rowTwo,
  speedOne = 0.16,
  speedTwo = 0.07,
  className = "",
}: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const rowOneRef = useRef<HTMLDivElement>(null);
  const rowTwoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const r1 = rowOneRef.current;
    const r2 = rowTwoRef.current;
    if (!section || !r1 || !r2) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // 0 when the section's top hits the bottom of the viewport,
      // 1 when its bottom leaves the top of the viewport.
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const travel = vh + rect.height;
      const mobile = window.innerWidth < 768;
      const scale = mobile ? 0.5 : 1;

      const dx1 = -(clamped - 0.5) * travel * speedOne * scale;
      const dx2 = (clamped - 0.5) * travel * speedTwo * scale;

      r1.style.transform = `translate3d(${dx1.toFixed(1)}px,0,0)`;
      r2.style.transform = `translate3d(${dx2.toFixed(1)}px,0,0)`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [speedOne, speedTwo]);

  const renderRow = (images: CollageImage[], ref: React.RefObject<HTMLDivElement | null>) => (
    <div className="overflow-hidden">
      <div
        ref={ref}
        className="flex gap-2 will-change-transform"
        style={{ justifyContent: "center" }}
      >
        {images.map((img, i) => (
          <div
            key={`${img.src}-${i}`}
            className="relative h-[240px] md:h-[400px] shrink-0 bg-muted/40 overflow-hidden"
            style={{ width: `${(img.span ?? 1) * BASE_TILE}px` }}
          >
            {img.video ? (
              <video
                src={img.src}
                aria-label={img.alt}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      aria-label="Field imagery"
      className={`relative w-full overflow-hidden bg-background py-2 ${className}`}
    >
      <div className="flex flex-col gap-2">
        {renderRow(rowOne, rowOneRef)}
        {renderRow(rowTwo, rowTwoRef)}
      </div>
    </section>
  );
}
